import * as THREE from "three";
import { pruneInPlace } from "../core/util.js";
import { A11Y } from "../core/a11y.js";

/**
 * Lightweight juice: floating damage/pickup text (billboarded sprites) and
 * small particle bursts on hits and deaths. Everything self-expires.
 *
 * PERF: geometries are shared module-level singletons (never re-created), and
 * floating-text TEXTURES are cached in an LRU keyed by (message,color) — damage
 * numbers repeat heavily, so a busy fight reuses one GPU upload instead of
 * building a new canvas + texture per number. Per-instance MATERIALS are still
 * created (opacity animates independently) but are cheap and disposed on expiry.
 */

// ---- shared geometries (one each, reused for every effect) ----
const BURST_GEO = new THREE.BoxGeometry(0.18, 0.18, 0.18);
const RING_GEO = new THREE.RingGeometry(0.9, 1.0, 32);
const FIELD_GEO = new THREE.PlaneGeometry(3.4, 2.8);

// ---- floating-text texture LRU cache ----
const _textCache = new Map();       // "color|msg" -> { tex, w, h }
const TEXT_CACHE_MAX = 160;
function textTex(msg, color) {
  const key = color + "|" + msg;
  const hit = _textCache.get(key);
  if (hit) { _textCache.delete(key); _textCache.set(key, hit); return hit; }   // LRU: bump to most-recent
  const font = "bold 40px Trebuchet MS";
  const H = 64, PAD = 22;
  const canvas = document.createElement("canvas");
  let c = canvas.getContext("2d");
  c.font = font;                                   // measure, then size (resizing resets the ctx)
  const textW = Math.ceil(c.measureText(msg).width);
  canvas.width = textW + PAD * 2; canvas.height = H;
  c = canvas.getContext("2d");
  c.font = font; c.textAlign = "center"; c.textBaseline = "middle";
  c.lineWidth = 6; c.strokeStyle = "#000";
  c.strokeText(msg, canvas.width / 2, H / 2);
  c.fillStyle = color; c.fillText(msg, canvas.width / 2, H / 2);
  const entry = { tex: new THREE.CanvasTexture(canvas), w: canvas.width, h: canvas.height };
  _textCache.set(key, entry);
  if (_textCache.size > TEXT_CACHE_MAX) {           // evict least-recently-used
    const oldest = _textCache.keys().next().value;
    const old = _textCache.get(oldest); _textCache.delete(oldest); old.tex.dispose();
  }
  return entry;
}

export class Effects {
  constructor(scene, cameraRig) {
    this.scene = scene;
    this.cameraRig = cameraRig;
    this.texts = [];
    this.bursts = [];
    this.rings = [];
    this.fields = [];
  }

  /** Remove all live effects immediately (used when tearing down a run). */
  clear() {
    for (const t of this.texts) { if (t.atlas) { this.scene.remove(t.mesh); t.mesh.geometry.dispose(); } else this.scene.remove(t.sprite); t.mat.dispose(); }
    for (const b of this.bursts) { b.bits.forEach((m) => this.scene.remove(m)); b.mat.dispose(); }
    for (const r of this.rings) { this.scene.remove(r.m); r.mat.dispose(); }
    for (const f of this.fields) { this.scene.remove(f.m); f.mat.dispose(); }
    this.texts = []; this.bursts = []; this.rings = []; this.fields = [];
  }

  _fieldTex() {
    if (this._fldTex) return this._fldTex;
    const c = document.createElement("canvas"); c.width = c.height = 128;
    const g = c.getContext("2d");
    const grad = g.createRadialGradient(64, 64, 6, 64, 64, 64);
    grad.addColorStop(0, "rgba(240,225,255,0.55)");
    grad.addColorStop(0.55, "rgba(199,125,255,0.30)");
    grad.addColorStop(1, "rgba(199,125,255,0)");
    g.fillStyle = grad; g.fillRect(0, 0, 128, 128);
    // faint hex-cell lines for a shield look
    g.strokeStyle = "rgba(230,205,255,0.35)"; g.lineWidth = 2;
    for (let i = 0; i < 3; i++) { g.beginPath(); g.ellipse(64, 64, 26 + i * 16, 30 + i * 18, 0, 0, Math.PI * 2); g.stroke(); }
    this._fldTex = new THREE.CanvasTexture(c);
    return this._fldTex;
  }

  /** A vertical energy panel that flashes where the player hits a barrier. */
  forceField(pos, dx, dz) {
    if (A11Y.reduceMotion) return;   // skip the repetitive barrier flash
    const mat = new THREE.MeshBasicMaterial({ map: this._fieldTex(), color: 0xc77dff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
    const m = new THREE.Mesh(FIELD_GEO, mat);
    m.rotation.y = Math.atan2(dx, dz);           // stand perpendicular to the push
    m.position.set(pos.x + dx * 0.6, 1.2, pos.z + dz * 0.6);
    this.scene.add(m);
    this.fields.push({ m, mat, life: 0.5, max: 0.5 });
  }

  /** Expanding ground ring — AoE / nova shockwaves. */
  ring(pos, color = "#ffffff", radius = 2.5) {
    const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending });
    const m = new THREE.Mesh(RING_GEO, mat);
    m.rotation.x = -Math.PI / 2;
    m.position.set(pos.x, (pos.y ?? 0) + 0.15, pos.z);
    this.scene.add(m);
    this.rings.push({ m, life: 0.45, max: 0.45, maxR: radius, mat });
  }

  /**
   * Floating text. ASCII strings (every damage number, "PARRY!", "+50 gold", …)
   * are assembled from a shared GLYPH ATLAS — no canvas or texture upload per
   * number, just a small quad buffer + a tint material. Strings containing emoji
   * / other non-ASCII fall back to the per-string cached canvas sprite below.
   */
  text(pos, message, color = "#ffffff") {
    const msg = String(message);
    if (this._atlasOk(msg)) {
      const geo = this._textGeo(msg);
      const mat = new THREE.MeshBasicMaterial({ map: this._glyphAtlas().tex, color: new THREE.Color(color), transparent: true, depthTest: false, depthWrite: false });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(pos.x, (pos.y ?? 0) + 2.2, pos.z);
      mesh.renderOrder = 999;
      this.scene.add(mesh);
      this.texts.push({ mesh, mat, life: 1.0, atlas: true });
    } else {
      this._textCanvas(pos, msg, color);
    }
  }

  _atlasOk(msg) {
    for (let i = 0; i < msg.length; i++) { const c = msg.charCodeAt(i); if (c !== 32 && (c < 33 || c > 126)) return false; }
    return true;
  }

  /** Legacy path: one canvas+texture per unique (message,color), LRU-cached. Only
   *  hit now by the handful of strings that carry emoji (🗝, ✦, …). */
  _textCanvas(pos, message, color) {
    const { tex, w, h } = textTex(message, color);
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
    const sprite = new THREE.Sprite(mat);
    sprite.position.set(pos.x, (pos.y ?? 0) + 2.2, pos.z);
    const worldH = 0.78;
    sprite.scale.set(worldH * (w / h), worldH, 1);
    sprite.renderOrder = 999;
    this.scene.add(sprite);
    this.texts.push({ sprite, life: 1.0, mat });   // NB: `tex` is cached/shared — never disposed here
  }

  /** One shared atlas of printable ASCII, built once. Glyphs are WHITE with a
   *  BLACK outline on transparent; tinting the material multiplies, so white fill
   *  becomes the tint colour while the outline stays black (readable on any bg). */
  _glyphAtlas() {
    if (this._atlas) return this._atlas;
    const FIRST = 33, LAST = 126, N = LAST - FIRST + 1, COLS = 16, ROWS = Math.ceil(N / COLS), CELL = 48;
    const AW = COLS * CELL, AH = ROWS * CELL;
    const cv = document.createElement("canvas"); cv.width = AW; cv.height = AH;
    const g = cv.getContext("2d");
    g.font = "bold 32px Trebuchet MS, sans-serif"; g.textAlign = "center"; g.textBaseline = "middle"; g.lineJoin = "round";
    const glyphs = {};
    for (let i = 0; i < N; i++) {
      const ch = String.fromCharCode(FIRST + i);
      const col = i % COLS, row = (i / COLS) | 0;
      const cx = col * CELL + CELL / 2, cy = row * CELL + CELL / 2;
      g.lineWidth = 5; g.strokeStyle = "#000"; g.strokeText(ch, cx, cy);
      g.fillStyle = "#fff"; g.fillText(ch, cx, cy);
      const adv = Math.min(CELL, Math.ceil(g.measureText(ch).width) + 7);
      glyphs[ch] = {
        u0: (col * CELL) / AW, u1: ((col + 1) * CELL) / AW,
        vt: 1 - (row * CELL) / AH, vb: 1 - ((row + 1) * CELL) / AH, adv,
      };
    }
    glyphs[" "] = { space: true, adv: CELL * 0.32 };
    const tex = new THREE.CanvasTexture(cv);
    tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false;
    this._atlas = { tex, glyphs, CELL };
    return this._atlas;
  }

  /** Build a centered billboard geometry of quads for `str` (fresh each call;
   *  disposed on expiry — cheap, and no texture work). */
  _textGeo(str) {
    const A = this._glyphAtlas(), CELL = A.CELL, SCALE = 0.024;   // px → world height (~0.78)
    let total = 0; const list = [];
    for (const ch of str) { const gg = A.glyphs[ch]; if (!gg) continue; list.push(gg); total += gg.adv; }
    const pos = [], uv = [], idx = []; let x = -total / 2, vi = 0;
    for (const gg of list) {
      if (!gg.space) {
        const x0 = x + gg.adv / 2 - CELL / 2, x1 = x0 + CELL, y0 = -CELL / 2, y1 = CELL / 2;
        pos.push(x0, y0, 0, x1, y0, 0, x1, y1, 0, x0, y1, 0);
        uv.push(gg.u0, gg.vb, gg.u1, gg.vb, gg.u1, gg.vt, gg.u0, gg.vt);
        idx.push(vi, vi + 1, vi + 2, vi, vi + 2, vi + 3); vi += 4;
      }
      x += gg.adv;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.scale(SCALE, SCALE, 1);
    return geo;
  }

  burst(pos, color = "#ffffff", count = 8) {
    if (A11Y.reduceMotion) count = Math.max(2, Math.round(count * 0.3));   // fewer flying bits
    const mat = new THREE.MeshBasicMaterial({ color });
    const bits = [];
    for (let i = 0; i < count; i++) {
      const m = new THREE.Mesh(BURST_GEO, mat);
      m.position.set(pos.x, (pos.y ?? 0) + 1, pos.z);
      const a = Math.random() * Math.PI * 2;
      const sp = 3 + Math.random() * 4;
      m.userData.v = new THREE.Vector3(Math.cos(a) * sp, 4 + Math.random() * 3, Math.sin(a) * sp);
      this.scene.add(m);
      bits.push(m);
    }
    this.bursts.push({ bits, life: 0.6, mat });
  }

  update(dt) {
    // floating text: rise + fade. Atlas meshes are billboarded to the camera each
    // frame (sprites face it automatically).
    const cam = this.cameraRig && this.cameraRig.camera;
    for (const t of this.texts) {
      t.life -= dt;
      const node = t.atlas ? t.mesh : t.sprite;
      node.position.y += dt * 1.6;
      if (t.atlas && cam) node.quaternion.copy(cam.quaternion);
      t.mat.opacity = Math.max(0, t.life);
    }
    pruneInPlace(this.texts, (t) => {
      if (t.life > 0) return true;
      if (t.atlas) { this.scene.remove(t.mesh); t.mesh.geometry.dispose(); t.mat.dispose(); }
      else { this.scene.remove(t.sprite); t.mat.dispose(); }
      return false;
    });

    // bursts: gravity + fade
    for (const b of this.bursts) {
      b.life -= dt;
      for (const m of b.bits) {
        m.userData.v.y -= 18 * dt;
        m.position.addScaledVector(m.userData.v, dt);
        const s = Math.max(0.01, b.life / 0.6);
        m.scale.setScalar(s);
      }
    }
    pruneInPlace(this.bursts, (b) => {
      if (b.life <= 0) { b.bits.forEach((m) => this.scene.remove(m)); b.mat.dispose(); return false; }
      return true;
    });

    // rings: expand + fade
    for (const r of this.rings) {
      r.life -= dt;
      const t = 1 - r.life / r.max;                 // 0 → 1
      r.m.scale.setScalar(0.2 + t * r.maxR);
      r.mat.opacity = Math.max(0, r.life / r.max) * 0.85;
    }
    pruneInPlace(this.rings, (r) => {
      if (r.life <= 0) { this.scene.remove(r.m); r.mat.dispose(); return false; }
      return true;
    });

    // force-field panels: flash in, ripple, fade
    for (const f of this.fields) {
      f.life -= dt;
      const t = 1 - f.life / f.max;                 // 0 → 1
      f.mat.opacity = Math.sin(Math.min(1, t) * Math.PI) * 0.9;
      f.m.scale.set(1 + t * 0.35, 1 + Math.sin(t * Math.PI * 3) * 0.06, 1);
    }
    pruneInPlace(this.fields, (f) => {
      if (f.life <= 0) { this.scene.remove(f.m); f.mat.dispose(); return false; }
      return true;
    });
  }
}
